import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import styles from './style.module.css';
import * as Routes from '../../../../../constants/routes';
import { float, numberWithCommas } from '../../../../../helpers';
import { formatMortgageTerm } from '../../../../../helpers/mortgage';

const parseNum = (val) => {
    if (!val) return 0;
    const cleaned = String(val).replace(/,/g, '');
    return parseFloat(cleaned) || 0;
};

export const formName = Routes.SUMMARY;

const amortizationOptions = Array.from({ length: 24 }, (_, i) => i + 2);

const paymentFrequencyOptions = [
    'Weekly',
    'Weekly (Accelerated)',
    'Bi-Weekly',
    'Bi-Weekly (Accelerated)',
    'Semi-Monthly',
    'Monthly'
];

// Editable text row
class TextRow extends Component {
    constructor(props) {
        super(props);
        this.state = { editing: false, editValue: '' };
    }

    startEdit() {
        this.setState({
            editing: true,
            editValue: this.props.rawValue !== undefined ? String(this.props.rawValue) : ''
        });
    }

    confirmEdit() {
        const { onSave } = this.props;
        if (onSave) onSave(this.state.editValue);
        this.setState({ editing: false });
    }

    handleKeyPress(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.confirmEdit();
        }
    }

    render() {
        const { label, value, odd } = this.props;
        const { editing, editValue } = this.state;

        return (
            <div className={odd ? styles.rowOdd : styles.row}>
                <span className={styles.rowLabel}>{label}</span>
                <div className={styles.rowRight}>
                    {editing ? (
                        <input
                            type='text'
                            className={styles.editInput}
                            value={editValue}
                            onChange={(e) => this.setState({ editValue: e.target.value })}
                            onKeyPress={(e) => this.handleKeyPress(e)}
                            aria-label={`Edit ${label}`}
                            autoFocus
                        />
                    ) : (
                        <span className={styles.rowValue}>{value}</span>
                    )}
                    <button
                        type='button'
                        className={styles.editButton}
                        onClick={() => editing ? this.confirmEdit() : this.startEdit()}
                        aria-label={editing ? `Confirm ${label}` : `Edit ${label}`}
                    >
                        <span className={editing ? styles.checkIcon : styles.editIcon} />
                    </button>
                </div>
            </div>
        );
    }
}

// Editable select row
class SelectRow extends Component {
    constructor(props) {
        super(props);
        this.state = { editing: false, editValue: '' };
    }

    startEdit() {
        this.setState({
            editing: true,
            editValue: this.props.rawValue !== undefined ? String(this.props.rawValue) : ''
        });
    }

    confirmEdit() {
        const { onSave } = this.props;
        if (onSave) onSave(this.state.editValue);
        this.setState({ editing: false });
    }

    render() {
        const { label, value, odd, options } = this.props;
        const { editing, editValue } = this.state;

        return (
            <div className={odd ? styles.rowOdd : styles.row}>
                <span className={styles.rowLabel}>{label}</span>
                <div className={styles.rowRight}>
                    {editing ? (
                        <select
                            className={styles.editSelect}
                            value={editValue}
                            onChange={(e) => this.setState({ editValue: e.target.value })}
                            aria-label={`Select ${label}`}
                        >
                            {options.map(opt => (
                                <option key={typeof opt === 'object' ? opt.value : opt} value={typeof opt === 'object' ? opt.value : opt}>
                                    {typeof opt === 'object' ? opt.label : opt}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <span className={styles.rowValue}>{value}</span>
                    )}
                    <button
                        type='button'
                        className={styles.editButton}
                        onClick={() => editing ? this.confirmEdit() : this.startEdit()}
                        aria-label={editing ? `Confirm ${label}` : `Edit ${label}`}
                    >
                        <span className={editing ? styles.checkIcon : styles.editIcon} />
                    </button>
                </div>
            </div>
        );
    }
}

export default class SummaryAnswers extends Component {
    render() {
        const {
            annualIncome = 0,
            monthlyDebt = 0,
            downPayment = 0,
            amortizationPeriod = 0,
            paymentFrequency = '',
            mortgageTerm = '',
            mortgageRate = 0,
            closingCost = 0,
            estimatedBudget = 0,
            mortgageTermOptions = [],
            mortgageTerms = [],
            paymentFrequencyNumMap = {},
            updateField
        } = this.props;

        const termSelectOptions = mortgageTermOptions.map(t => ({ value: t, label: t }));
        const amortSelectOptions = amortizationOptions.map(y => ({ value: String(y), label: `${y} Years` }));

        return (
            <div className={styles.form}>
                <div className={styles.titleRow}>
                    <h2 className={styles.title}>Your Answers</h2>
                    <div className={styles.actions}>
                        <Link to={`${process.env.PUBLIC_URL}/mortgage${Routes.STEPONE}`} className={styles.actionLink}>
                            <span className={styles.startOverIcon} />
                            Start Over
                        </Link>
                        <button onClick={() => window.print()} className={styles.actionLink} type='button'>
                            <span className={styles.printIcon} />
                            Print
                        </button>
                    </div>
                </div>
                <div className={styles.table}>
                    <TextRow
                        label='Household Gross Income'
                        value={`$${numberWithCommas(annualIncome)}`}
                        rawValue={annualIncome}
                        onSave={(val) => updateField(Routes.STEPONE, 'annualIncome', parseNum(val))}
                    />
                    <TextRow
                        label='Monthly Debt'
                        value={`$${numberWithCommas(monthlyDebt)}`}
                        rawValue={monthlyDebt}
                        odd
                        onSave={(val) => {
                            const parsed = parseNum(val);
                            updateField(Routes.STEPTWO, 'loans', parsed);
                            updateField(Routes.STEPTWO, 'carPayments', 0);
                            updateField(Routes.STEPTWO, 'linesOfCredit', 0);
                            updateField(Routes.STEPTWO, 'creditCards', 0);
                            updateField(Routes.STEPTWO, 'other', 0);
                        }}
                    />
                    <TextRow
                        label='Downpayment'
                        value={`$${numberWithCommas(downPayment)}`}
                        rawValue={downPayment}
                        onSave={(val) => updateField(Routes.STEPFOUR, 'downPayment', parseNum(val))}
                    />
                    <SelectRow
                        label='Amortization Period'
                        value={`${amortizationPeriod} Years`}
                        rawValue={String(amortizationPeriod)}
                        odd
                        options={amortSelectOptions}
                        onSave={(val) => updateField(Routes.STEPFIVE, 'amortizationPeriod', parseInt(val, 10))}
                    />
                    <SelectRow
                        label='Payment Frequency'
                        value={paymentFrequency}
                        rawValue={paymentFrequency}
                        options={paymentFrequencyOptions}
                        onSave={(val) => {
                            updateField(Routes.STEPSIX, 'paymentFrequency', val);
                            updateField(Routes.STEPSIX, 'paymentFrequencyNum', paymentFrequencyNumMap[val]);
                        }}
                    />
                    <SelectRow
                        label='Mortgage Term'
                        value={mortgageTerm}
                        rawValue={mortgageTerm}
                        odd
                        options={termSelectOptions}
                        onSave={(val) => {
                            updateField(Routes.STEPSEVEN, 'mortgageTerm', val);
                            const newTerm = mortgageTerms.find(({ Range, SubType }) => formatMortgageTerm(Range, SubType) === val);
                            if (newTerm) {
                                updateField(Routes.STEPEIGHT, 'mortgageRate', float(newTerm.Rate1).toFixed(2));
                            }
                        }}
                    />
                    <TextRow
                        label='Mortgage Rate'
                        value={`${mortgageRate}%`}
                        rawValue={mortgageRate}
                        onSave={(val) => updateField(Routes.STEPEIGHT, 'mortgageRate', parseFloat(val) || 0)}
                    />
                    <TextRow
                        label='Closing Costs'
                        value={`$${numberWithCommas(closingCost)}`}
                        rawValue={closingCost}
                        odd
                        onSave={(val) => updateField(Routes.STEPNINE, 'closingCost', parseNum(val))}
                    />
                </div>
                <div className={styles.budgetRow}>
                    <span className={styles.budgetLabel}>Estimated Home Budget</span>
                    <span className={styles.budgetValue}>{`$${numberWithCommas(Math.max(estimatedBudget, 0))}`}</span>
                </div>
                {estimatedBudget < 0 && (
                    <p className={styles.budgetWarning}>Your estimated closing costs exceed your home budget. Consider adjusting your inputs.</p>
                )}
            </div>
        );
    }
}
